import { v4 as uuidv4 } from "uuid";

function parseCookies() {
  if (typeof document === "undefined") return {};
  return document.cookie.split(";").reduce((acc, part) => {
    const [rawKey, ...rest] = part.trim().split("=");
    if (!rawKey) return acc;
    acc[rawKey] = decodeURIComponent(rest.join("=") || "");
    return acc;
  }, {});
}

export function getCookie(name) {
  return parseCookies()[name] || "";
}

export function removeCookie(name) {
  setCookie(name, "", -1);
}

export function setCookie(name, value, daysOrOptions = 7) {
  if (typeof document === "undefined") return;

  let maxAge;
  if (typeof daysOrOptions === "object" && daysOrOptions !== null) {
    if ("maxAgeSeconds" in daysOrOptions) {
      maxAge = Math.max(0, Math.floor(Number(daysOrOptions.maxAgeSeconds) || 0));
    } else {
      maxAge = (Number(daysOrOptions.days) || 7) * 24 * 60 * 60;
    }
  } else {
    const days = Number(daysOrOptions);
    maxAge = days < 0 ? 0 : days * 24 * 60 * 60;
  }

  document.cookie = `${name}=${encodeURIComponent(
    value ?? ""
  )}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

export function getAuthToken() {
  if (typeof window === "undefined") return "";
  return getCookie("NHauth_token") || "";
}

export function getUserId() {
  if (typeof window === "undefined") return "";
  return getCookie("user_id") || "";
}

export function isLoggedIn() {
  return Boolean(getAuthToken());
}

export function normalizeUserDetails(raw = {}) {
  if (!raw || typeof raw !== "object") return null;

  const firstName = String(
    raw.firstName || raw.FirstName || raw.name || raw.userName || raw.UserName || ""
  ).trim();
  const lastName = String(raw.lastName || raw.LastName || "").trim();
  const email = String(raw.email || raw.emailId || raw.Email || "").trim();
  const mobileNumber = String(
    raw.mobileNumber || raw.phone || raw.Phone || ""
  ).trim();

  if (!firstName && !lastName && !email && !mobileNumber) return null;

  return {
    firstName,
    lastName,
    email,
    mobileNumber,
  };
}

export function getUserDetails() {
  if (typeof window === "undefined") return null;

  try {
    const raw = getCookie("nh_user_details");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getUserDisplayName() {
  if (!isLoggedIn()) return "";

  const details = getUserDetails();
  if (details?.firstName) return details.firstName;

  if (details?.email) {
    const localPart = details.email.split("@")[0]?.trim();
    if (localPart) return localPart;
  }

  return "";
}

export function formatNavUserName(name = "") {
  const trimmed = String(name || "").trim();
  const display = trimmed || "User";
  if (display.length <= 8) return display;
  return `${display.slice(0, 8)}...`;
}

export function getNavUserName() {
  if (!isLoggedIn()) return "User";

  const details = getUserDetails();
  const firstName = details?.firstName?.trim();
  return formatNavUserName(firstName || "User");
}

export const SESSION_EXPIRE_DATETIME_KEY = "sessionExpireDateTime";

const LEGACY_SESSION_EXPIRES_AT_KEY = "nh_session_expires_at";
const LEGACY_ACCESS_TOKEN_KEY = "nh_access_token";
const LEGACY_USER_ID_KEY = "user_id";
const LEGACY_GUEST_ID_STORAGE_KEY = "guest_id";

export const LEGACY_LOCAL_STORAGE_KEYS = [
  LEGACY_ACCESS_TOKEN_KEY,
  LEGACY_USER_ID_KEY,
  LEGACY_SESSION_EXPIRES_AT_KEY,
  LEGACY_GUEST_ID_STORAGE_KEY,
];

function parseSessionExpireDateTime(iso = "") {
  const trimmed = String(iso || "").trim();
  if (!trimmed) return 0;
  const expiresAt = Date.parse(trimmed);
  return Number.isFinite(expiresAt) && expiresAt > 0 ? expiresAt : 0;
}

function getSessionCookieMaxAgeSeconds(expiresAtMs) {
  if (!expiresAtMs) return 7 * 24 * 60 * 60;
  return Math.max(1, Math.floor((expiresAtMs - Date.now()) / 1000));
}

export function setSessionExpireDateTime(sessionExpireDateTime) {
  if (typeof window === "undefined") return;
  const iso = String(sessionExpireDateTime || "").trim();
  const expiresAtMs = parseSessionExpireDateTime(iso);
  if (!expiresAtMs) return;

  setCookie(SESSION_EXPIRE_DATETIME_KEY, iso, {
    maxAgeSeconds: getSessionCookieMaxAgeSeconds(expiresAtMs),
  });
}

export function getSessionExpireDateTime() {
  if (typeof window === "undefined") return "";
  return getCookie(SESSION_EXPIRE_DATETIME_KEY) || "";
}

export function getSessionExpiresAt() {
  return parseSessionExpireDateTime(getSessionExpireDateTime());
}

export function clearSessionExpireDateTime() {
  if (typeof window === "undefined") return;
  setCookie(SESSION_EXPIRE_DATETIME_KEY, "", -1);
}

/** One-time sweep of keys previously stored in localStorage (e.g. on app boot). */
export function clearLegacyLocalStorage() {
  if (typeof localStorage === "undefined") return;
  LEGACY_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}

function clearLegacyAuthStorage() {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
  localStorage.removeItem(LEGACY_USER_ID_KEY);
  localStorage.removeItem(LEGACY_SESSION_EXPIRES_AT_KEY);
}

function clearLegacyGuestStorage() {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(LEGACY_GUEST_ID_STORAGE_KEY);
}

export function isAuthSessionExpired() {
  if (!getAuthToken()) return false;
  const expiresAt = getSessionExpiresAt();
  if (!expiresAt) return false;
  return Date.now() >= expiresAt;
}

export function setAuthSession({
  token = "",
  userId = "",
  userDetails = null,
  sessionExpireDateTime = "",
} = {}) {
  if (typeof window === "undefined") return;

  const nextExpireIso = String(sessionExpireDateTime || "").trim();
  let expiresAtMs = parseSessionExpireDateTime(nextExpireIso);
  if (!expiresAtMs) {
    expiresAtMs = getSessionExpiresAt();
  }

  const maxAgeSeconds = getSessionCookieMaxAgeSeconds(expiresAtMs);
  const cookieOpts = { maxAgeSeconds };

  if (nextExpireIso) {
    setSessionExpireDateTime(nextExpireIso);
  }

  if (token) {
    setCookie("NHauth_token", token, cookieOpts);
    clearLegacyAuthStorage();
  }

  if (userId) {
    setCookie("user_id", userId, cookieOpts);
  }

  const normalizedDetails = normalizeUserDetails(userDetails);
  if (normalizedDetails) {
    setCookie("nh_user_details", JSON.stringify(normalizedDetails), cookieOpts);
  }
}

export function clearAuthSession() {
  if (typeof window === "undefined") return;

  clearLegacyAuthStorage();
  clearSessionExpireDateTime();
  setCookie("NHauth_token", "", -1);
  setCookie("user_id", "", -1);
  setCookie("nh_user_details", "", -1);
}

export const GUEST_ID_COOKIE = "guestId";
const LEGACY_GUEST_ID_COOKIE = "guest_id";

/** Read guest / temp user id from cookies only (no create). */
export function readGuestIdFromCookies() {
  if (typeof window === "undefined") return "";
  return (
    getCookie(GUEST_ID_COOKIE) ||
    getCookie(LEGACY_GUEST_ID_COOKIE) ||
    ""
  );
}

/**
 * Returns existing guest id from cookies, or creates one and stores it in `guestId` cookie.
 * Never reads or writes localStorage.
 */
export function getGuestId() {
  if (typeof window === "undefined") return "";

  const existing = readGuestIdFromCookies();
  if (existing) {
    clearLegacyGuestStorage();
    if (!getCookie(GUEST_ID_COOKIE) && getCookie(LEGACY_GUEST_ID_COOKIE)) {
      setCookie(GUEST_ID_COOKIE, existing, 7);
    }
    return existing;
  }

  const id = uuidv4();
  setCookie(GUEST_ID_COOKIE, id, 7);
  clearLegacyGuestStorage();
  return id;
}

import { isIndiaRestrictedPath } from "./region";

export function withRegionPath(path = "/") {
  if (typeof window === "undefined") return path;
  const isIndia = window.location.pathname.startsWith("/in");
  if (!isIndia) return path;
  if (path.startsWith("/in")) return path;
  if (isIndiaRestrictedPath(path)) return path;
  return `/in${path}`;
}
